import React, { useState } from "react";
import { Play } from "lucide-react";
import Seo from "@/components/Seo";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";

// Een cam toevoegen/activeren = youtubeId invullen. Alleen cams met een
// geldige youtubeId worden getoond (geen lege/placeholder-speler).
const CAMS = [
  // Lemster Toer staat tijdelijk uit: stream privé, geen openbare ID.
  // Later terugzetten = hier de nieuwe openbare youtubeId invullen.
  { key: "lemster-toer", title: "Lemster Toer", youtubeId: "" },
  { key: "ijsselmeer", title: "IJsselmeer", youtubeId: "m56IrgAhzuI" },
  { key: "binnenhaven", title: "Binnenhaven", youtubeId: "" },
];

const activeCams = CAMS.filter((cam) => Boolean(cam.youtubeId));

const videoJsonLd = (cam, description) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: `Live webcam ${cam.title} Lemmer`,
  description,
  thumbnailUrl: `https://i.ytimg.com/vi/${cam.youtubeId}/hqdefault.jpg`,
  embedUrl: `https://www.youtube-nocookie.com/embed/${cam.youtubeId}`,
  isLiveBroadcast: true,
  publisher: {
    "@type": "Organization",
    name: "Lemmer Leeft",
    url: "https://lemmer-leeft.nl",
  },
});

// Privacyvriendelijke facade: eerst alleen de thumbnail (i.ytimg.com),
// het YouTube-iframe laadt pas na een klik (nocookie-domein).
const CamTile = ({ cam, playLabel, liveBadge }) => {
  const [playing, setPlaying] = useState(false);

  return (
    <figure data-testid={`cam-tile-${cam.key}`} className="group">
      <figcaption className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 bg-[#CE1B2A] text-white text-[11px] font-[Poppins] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
          {liveBadge}
        </span>
        <h2 className="font-[Poppins] font-bold text-[#173A5E] text-lg">
          {cam.title}
        </h2>
      </figcaption>
      <div
        className="relative w-full overflow-hidden rounded-2xl bg-[#173A5E] shadow-sm"
        style={{ aspectRatio: "16 / 9" }}
      >
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${cam.youtubeId}?autoplay=1&mute=1&rel=0`}
            title={`Live webcam ${cam.title} Lemmer`}
            loading="lazy"
            allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 w-full h-full border-0"
            data-testid={`cam-iframe-${cam.key}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`${playLabel}: ${cam.title}`}
            data-testid={`cam-play-${cam.key}`}
            className="absolute inset-0 w-full h-full cursor-pointer"
          >
            <img
              src={`https://i.ytimg.com/vi/${cam.youtubeId}/hqdefault.jpg`}
              alt={`Webcam ${cam.title} Lemmer`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
            <span
              className="absolute inset-0 bg-[#173A5E]/20 group-hover:bg-[#173A5E]/10 transition-colors"
              aria-hidden="true"
            />
            <span
              className="absolute inset-0 flex items-center justify-center"
              aria-hidden="true"
            >
              <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#CE1B2A] text-white flex items-center justify-center shadow-xl transition-transform duration-300 group-hover:scale-110">
                <Play className="w-7 h-7 sm:w-8 sm:h-8 ml-1 fill-current" />
              </span>
            </span>
            <span className="absolute bottom-3 left-3 right-3 text-left">
              <span className="inline-block bg-[#173A5E]/85 text-white font-[Poppins] font-medium text-xs sm:text-sm px-3 py-1.5 rounded-full backdrop-blur-sm">
                {playLabel}
              </span>
            </span>
          </button>
        )}
      </div>
    </figure>
  );
};

export const WebcamsPage = () => {
  const { lang } = useLang();
  const c = pickLang(pageContent.webcams, lang);
  // "BIZ Lemmer" wordt een interne link naar /over-ons; de rest blijft platte tekst.
  const creditParts = (c.credit || "").split("BIZ Lemmer");

  return (
    <div data-testid="webcams-page" className="bg-white">
      <Seo
        title={c.seo?.title}
        description={c.seo?.description}
        lang={lang}
        jsonLd={activeCams.map((cam) => videoJsonLd(cam, c.seo?.description))}
      />

      {/* Sfeeraccent */}
      <section
        className="relative w-full overflow-hidden"
        style={{ height: "clamp(220px, 34vw, 380px)" }}
      >
        <img
          src="/media/lemmer-haven-hero.jpg"
          alt="De haven van Lemmer"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(23,58,94,0.15) 0%, rgba(23,58,94,0.75) 100%)",
          }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="ll-container pb-10">
            <span
              data-testid="webcams-hero-eyebrow"
              className="inline-block text-[#6BC0E8] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3"
            >
              {c.heroEyebrow}
            </span>
            <h1
              data-testid="webcams-h1"
              className="text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight"
            >
              {c.h1}
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          <p
            data-testid="webcams-intro"
            className="text-[#3E5871] font-[Inter] text-lg sm:text-xl leading-relaxed"
          >
            {c.intro}
          </p>
        </div>
      </section>

      {/* Cam-grid */}
      <section className="pb-14 sm:pb-20">
        <div className="ll-container">
          <div className="grid gap-8 sm:gap-10 md:grid-cols-2">
            {activeCams.map((cam) => (
              <CamTile
                key={cam.key}
                cam={cam}
                playLabel={c.playLabel}
                liveBadge={c.liveBadge}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Credit — platte tekst, alleen "BIZ Lemmer" linkt intern naar /over-ons */}
      <section
        data-testid="webcams-credit"
        className="ll-section-soft py-12 sm:py-14"
      >
        <div className="ll-container max-w-3xl text-center">
          <p className="text-[#173A5E] font-[Poppins] font-semibold text-base sm:text-lg">
            {creditParts[0]}
            <LLink
              to="/over-ons"
              data-testid="webcams-credit-over-ons"
              className="text-[#2E9CD6] hover:text-[#173A5E] underline underline-offset-2 transition"
            >
              BIZ Lemmer
            </LLink>
            {creditParts[1]}
          </p>
        </div>
      </section>
    </div>
  );
};

export default WebcamsPage;
